import { Trash } from 'lucide-react';
import moment from 'moment';
import React from 'react';
import { Field } from '../../../atoms/forms';
import { Product } from '../../../atoms/product';
import Add from '../../../components_v2/add/Add';
import Dropdown from '../../../components_v2/dropdown/Dropdown';
import { DropdownData, DropdownStyle } from '../../../components_v2/dropdown/model/Model';
import { ButtonStyle } from '../../../components_v2/popup/PopupCreation';
import { DefaultButton } from '../../../styles/global/css/GlobalButton';
import { Loader } from '../../../styles/global/css/GlobalLoader';
import { RedSidely } from '../../../styles/global/css/Utils';
import { Translate, translateToString } from '../../../styles/global/translate';
import { importWrapper } from '../../../utils/import';
import { AlertCall, AlertRes } from '../../alert/AlertProvider';
import { SelectionAction } from '../../client-companies/style/Style';
import { creationPopupDropdownStyle } from '../../../components_v2/popup/style/CreationFormStyle';
import { launchToastError } from '../../forms/visual-form-editor/visualFormEditor';
import { goalPathHash } from '../../reports/interpretor/ReportInterpretor';
import { importAdditionalValues } from '../action';
import { doParseValue, PathAndValue } from '../assortmentV3';
import { FlexDiv } from '../style';

type TemplateImport = { [key: string]: unknown }

function HeaderAndFieldSelector<Header, Field>(props: {
	index: number,
	dropdownStyle: DropdownStyle,
	value: [Field, Header, number],
	headerDD: DropdownData<Header>[],
	setValues: React.Dispatch<React.SetStateAction<[Field, Header, number][]>>,
	fieldDD: DropdownData<Field>[],
}) {
	const { index, value } = props;
	const [fieldId, headerIndex] = value;
	return <FlexDiv gap='10px' style={{ width: '100%' }}>
		<Dropdown
			label={index === 0 ? translateToString('column') : undefined}
			dropdownStyle={props.dropdownStyle}
			name={'headerDD$' + index}
			datalist={props.headerDD}
			selectedValue={props.headerDD.find(header => header.value === headerIndex)}
			onChange={(v) => {
				value[1] = v.value;
				if (!value[0]) {
					const fieldWithSameName = props.fieldDD.find(f => f.label === v.label);
					if (fieldWithSameName) value[0] = fieldWithSameName.value;
				}
				props.setValues(v => [...v]);
			}}
		/>
		<Dropdown
			label={index === 0 ? translateToString('report_editor.form_field') : undefined}
			dropdownStyle={props.dropdownStyle}
			name={'fieldDD$' + index}
			datalist={props.fieldDD}
			selectedValue={props.fieldDD.find(data => data.value === fieldId)}
			onChange={(v) => {
				value[0] = v.value;
				props.setValues(v => [...v]);
			}}
		/>
		<SelectionAction
			onClick={() => props.setValues(v => v.filter((_, i) => index !== i))}
			style={{ flexShrink: 0, marginTop: index === 0 ? 21 : 0 }}
			hoverSvgColor={RedSidely}
		>
			<Trash />
		</SelectionAction>
	</FlexDiv>;
}

async function transformAndImportData(
	data: unknown[],
	productNameMap: Map<string, string>,
	productBarcodeMap: Map<string, string>,
	alert: AlertCall,
	fields: Field[]
): Promise<boolean> {
	const fieldMap = new Map(fields.map(f => [f.id, f]));
	const headers = data[0] as unknown[];
	const alertRes = await alert<PathAndValue[]>({
		width: '750px',
		title: <Translate id='general.import' />,
		buttonMargin: '0px',
		noButtons: true,
		overflow: 'unset',
		content: (onBreak, onOk) => {
			const [step, setStep] = React.useState<number>(0);
			const [productColumn, setProductColumn] = React.useState<number>();
			// [fieldId, headerIndex, uniqueId]
			const [pathColumns, setPathColumns] = React.useState<[number | undefined, number | undefined, number][]>([]);
			const [valueColumns, setValueColumns] = React.useState<[number | undefined, number | undefined, number][]>([]);
			const [keepEmpty, setKeepEmpty] = React.useState<boolean>(false);

			const [additionalValues, setAdditionalValues] = React.useState<PathAndValue[]>();
			const [duplicates, setDuplicates] = React.useState<Set<PathAndValue['path']>>(new Set());
			const [importErrors, setImportErrors] = React.useState<string[]>([]);

			const fieldDropdownData = React.useMemo(() => fields.map(field => ({ value: field.id, label: field.name })), [fields]);
			const headerDropdownData = React.useMemo(() => headers.map((header, index) => ({ value: index, label: header?.toString() ?? '' })), [headers]);
			const dropdownStyle: DropdownStyle = { ...creationPopupDropdownStyle, containerHeight: 'fit-content' };

			// choose product column
			if (step === 0) {
				return <FlexDiv flow='column' gap='10px' align='flex-start' style={{ width: '100%', textAlign: 'initial' }}>
					<h4 style={{ margin: 0 }}><Translate id='import.choose_product_column' /></h4>
					<Dropdown
						dropdownStyle={dropdownStyle}
						name={''}
						datalist={headerDropdownData}
						selectedValue={headerDropdownData.find(data => data.value === productColumn)}
						onChange={(value) => setProductColumn(value.value)}
					/>
					<FlexDiv justify='space-between' style={{ width: '100%' }}>
						<DefaultButton buttonStyle={ButtonStyle.White} margin='15px 0 0 0' disabled><Translate id='previous' /></DefaultButton>
						<DefaultButton margin='15px 0 0 0' disabled={productColumn === undefined} onClick={() => setStep(1)}><Translate id='next' /></DefaultButton>
					</FlexDiv>
				</FlexDiv>;
			}

			// set path
			if (step === 1) {
				const filteredHeaderDropdownData = headerDropdownData.filter(data => data.value !== productColumn);
				return <FlexDiv flow='column' gap='10px' align='flex-start' style={{ width: '100%', textAlign: 'initial' }} >
					<h4 style={{ margin: 0 }}><Translate id='import.choose_static_values' /></h4>
					{pathColumns.map((value, index) => (
						<HeaderAndFieldSelector
							key={value[2]}
							value={value}
							index={index}
							dropdownStyle={dropdownStyle}
							headerDD={filteredHeaderDropdownData}
							setValues={setPathColumns}
							fieldDD={fieldDropdownData}
						/>
					))}
					<Add onClick={() => setPathColumns([...pathColumns, [undefined, undefined, Date.now()]])} />
					<FlexDiv justify='space-between' style={{ width: '100%' }}>
						<DefaultButton buttonStyle={ButtonStyle.White} margin='15px 0 0 0' onClick={() => setStep(0)}><Translate id='previous' /></DefaultButton>
						<DefaultButton margin='15px 0 0 0' disabled={pathColumns.some(p => p[0] === undefined || p[1] === undefined)} onClick={() => setStep(2)}><Translate id='next' /></DefaultButton>
					</FlexDiv>
				</FlexDiv>;
			}

			// choose values to import
			if (step === 2) {
				const filteredFieldDropdownData = fieldDropdownData.filter(data => !pathColumns.some(p => p[0] === data.value));
				const filteredHeaderDropdownData = headerDropdownData.filter(data => data.value !== productColumn && !pathColumns.some(p => p[1] === data.value));

				return <FlexDiv flow='column' gap='10px' align='flex-start' style={{ width: '100%', textAlign: 'initial' }} >
					<h4 style={{ margin: 0 }}><Translate id='import.choose_values_to_import' /></h4>
					{valueColumns.map((value, index) => (
						<HeaderAndFieldSelector
							key={value[2]}
							value={value}
							index={index}
							dropdownStyle={dropdownStyle}
							headerDD={filteredHeaderDropdownData}
							setValues={setValueColumns}
							fieldDD={filteredFieldDropdownData}
						/>
					))}
					<Add onClick={() => setValueColumns([...valueColumns, [undefined, undefined, Date.now()]])} />
					<label className="text-sm flex items-center">
						<Translate id='import.keep_empty' />
						<input className="m-2" type="checkbox" onChange={e => setKeepEmpty(e.target.checked)} /></label>
					<FlexDiv justify='space-between' style={{ width: '100%' }}>
						<DefaultButton buttonStyle={ButtonStyle.White} margin='15px 0 0 0' onClick={() => setStep(1)}><Translate id='previous' /></DefaultButton>
						<DefaultButton margin='15px 0 0 0' disabled={valueColumns.length === 0 || valueColumns.some(p => p[0] === undefined || p[1] === undefined)} onClick={() => {
							setStep(3);
							const errors: string[] = [];
							const av = data.reduce<PathAndValue[]>((acc, v, index) => {
								if (index === 0) return acc;
								if (!Array.isArray(v)) return acc;
								if (v.length === 0 || v.every(cell => cell === undefined || cell === null || cell === '')) return acc;
								const rawProduct = v[productColumn!];
								// xlsx numeric cells (typically barcodes) come out as numbers; the maps are string-keyed
								const productId = productNameMap.get(rawProduct) ?? productBarcodeMap.get(rawProduct)
									?? productNameMap.get(rawProduct?.toString()) ?? productBarcodeMap.get(rawProduct?.toString());
								if (productId === undefined) {
									errors.push(translateToString('import.multi_dimensional_assortments.errors.unknown_product')
										.replace('{{ ROW }}', (index + 1).toString())
										.replace('{{ PRODUCT }}', rawProduct?.toString() ?? ''));
									return acc;
								}
								const path = { p: productId };

								try {
									for (const [fieldId, columnIndex] of pathColumns) {
										path[fieldId!] = doParseValue(fieldMap.get(fieldId!)!.type, v[columnIndex!]);
									}
									for (const [fieldId, columnIndex] of valueColumns) {
										if (!keepEmpty && (v[columnIndex!] === undefined || v[columnIndex!] === null)) continue;
										acc.push({ path: { ...path, f: fieldId }, value: doParseValue(fieldMap.get(fieldId!)!.type, v[columnIndex!]) ?? null });
									}
								} catch (e) {
									errors.push(translateToString('import.multi_dimensional_assortments.errors.bad_value')
										.replace('{{ ROW }}', (index + 1).toString())
										.replace('{{ ERROR }}', e instanceof Error ? e.message : String(e)));
								}
								return acc;
							}, []);

							// check Av path duplicates
							const seen = new Set<string>();
							const duplicates = av.reduce<Set<PathAndValue['path']>>((acc, { path }) => {
								const key = goalPathHash(path);
								if (seen.has(key)) {
									acc.add(path);
									return acc;
								}
								seen.add(key);
								return acc;
							}, new Set());
							setDuplicates(duplicates);
							setAdditionalValues(av);
							setImportErrors(errors);
							if (duplicates.size === 0 && errors.length === 0) {
								onOk(av);
							}
						}
						}><Translate id='next' /></DefaultButton>
					</FlexDiv>
				</FlexDiv>;
			}

			// review skipped rows and duplicates before importing what remains
			if (step === 3 && (duplicates.size !== 0 || importErrors.length !== 0)) {
				const importable = additionalValues?.filter(({ path }) => !duplicates.has(path)) ?? [];
				return <FlexDiv flow='column' gap='10px' align='flex-start' style={{ width: '100%', textAlign: 'initial' }}>
					{importErrors.length !== 0 && <>
						<h4 style={{ margin: 0 }}><Translate id='import.multi_dimensional_assortments.errors.found' /></h4>
						<FlexDiv flow='column' gap='2px' align='flex-start' style={{ maxHeight: 200, overflowY: 'auto', width: '100%', fontSize: 12 }}>
							{importErrors.slice(0, 20).map((error, i) => <span key={i}>{error}</span>)}
							{importErrors.length > 20 && <span>{translateToString('import.multi_dimensional_assortments.errors.more').replace('{{ COUNT }}', (importErrors.length - 20).toString())}</span>}
						</FlexDiv>
					</>}
					{duplicates.size !== 0 && <>
						<h4 style={{ margin: 0 }}><Translate id='import.multi_dimensional_assortments.duplicates.found' /></h4>
						<p style={{ marginBottom: 10 }}><Translate id='import.multi_dimensional_assortments.duplicates.p1' /></p>
					</>}
					<FlexDiv justify='space-between' style={{ width: '100%' }}>
						<DefaultButton buttonStyle={ButtonStyle.White} margin='15px 0 0 0' onClick={() => onBreak()}><Translate id='cancel' /></DefaultButton>
						<DefaultButton margin='15px 0 0 0' disabled={importable.length === 0} onClick={() => onOk(importable)}>
							{translateToString('import.multi_dimensional_assortments.import_valid').replace('{{ COUNT }}', importable.length.toString())}
						</DefaultButton>
					</FlexDiv>
				</FlexDiv>;
			}

			return <FlexDiv width='100%' justify='center'><Loader width='40px' /></FlexDiv>;
		},
	});
	if (alertRes.res == AlertRes.Break) return false;
	await importAdditionalValues(alertRes.data);
	return true;
}

function genFakeValueForField(field: Field): unknown {
	const now = moment();
	switch (field.type) {
		case 'Text':
		case 'TextArea':
			return 'Test text field';
		case 'Integer':
			return Math.floor(Math.random() * 100);
		case 'Money':
			return Number.parseFloat(((Math.random() + 1) * 100).toFixed(2));
		case 'Boolean':
			return true;
		case 'Number':
			return Math.random() * 100;
		case 'Date':
			return now.toISOString();
		case 'Select': {
			const data = field.data as string[] | undefined;
			return data?.[0];
		}
	}
}

export default function importMultiDimensionalAssortments(onImport: (imported: boolean) => void, products: Product[], alert: AlertCall, fields: Field[]) {
	const productNameMap = new Map(products.filter(p => p.name).map(p => [p.name!, p.uuid]));
	const productBarcodeMap = new Map(products.filter(p => p.barcode).map(p => [p.barcode!, p.uuid]));
	importWrapper(
		(data) => transformAndImportData(data, productNameMap, productBarcodeMap, alert, fields)
			.catch((e): boolean => {
				launchToastError(String(e));
				return false;
			})
			.then(onImport),
		alert,
		() => {
			// put Select fields in first and take top 3
			const promoFields = [...fields].sort((a, b) => a.type === 'Select' ? -1 : b.type === 'Select' ? 1 : 0).slice(0, 3);
			const valueField = fields.find(f => f.type === 'Integer' || f.type === 'Number' || f.type === 'Money');
			const productsWithBarcode = products.filter(p => p.name);

			const baseRow = {};
			promoFields.forEach(field => {
				baseRow[field.name] = genFakeValueForField(field);
			});

			const rows: TemplateImport[] = [];
			for (let i = 0; i < 20; i++) {
				if (i >= productsWithBarcode.length) {
					return rows;
				}
				if (valueField) {
					baseRow[valueField.name] = genFakeValueForField(valueField);
				} else {
					baseRow['value'] = 'TEST VALUE';
				}
				rows.push({
					name: productsWithBarcode[i].name,
					...baseRow
				});
			}
			return rows;
		}
	);
}
